import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import url from '../../../../serverUrl';
import { api as axios } from '../../../../api';
import { fmtPct, fmtPrice, fmtUsd, num, quoteSymbolFor } from './format';
import './pro.css';

// ---------------------------------------------------------------------------
// Live ticker across the top of the exchange (same idea as the hootdex.net
// home-page marquee, tuned for traders):
//
//   exchange-wide  24H volume, 1H volume, 24H trades, order book depth,
//                  HD Vault liquidity, PECU price      (/wallet/platform-stats)
//   movers         top gainer / top loser                (/hootdex/market-tickers)
//   markets        most active markets: pair, live price, 24h change - click
//                  to open that market
//
// Every figure is measured server-side; missing values are left out rather
// than shown as a confident 0.
// ---------------------------------------------------------------------------

const MAX_MARKETS = 16;

function usePoll(fn, ms) {
  useEffect(() => {
    let cancelled = false;
    let timer;
    const tick = () =>
      Promise.resolve(fn(() => cancelled))
        .catch(() => {})
        .finally(() => {
          if (!cancelled) timer = setTimeout(tick, ms);
        });
    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function Change({ v }) {
  if (num(v) === null) return null;
  return <span className={num(v) >= 0 ? 'x-up' : 'x-down'}>{fmtPct(v)}</span>;
}

export default function MarketMarquee() {
  const navigate = useNavigate();
  const { PecuPrice, tokenList } = useSelector((s) => s);
  const [platform, setPlatform] = useState(null);
  const [tickers, setTickers] = useState([]);

  // Keep the last good reading per field if a poll fails.
  usePoll(
    (cancelled) =>
      axios.get(`${url}/wallet/platform-stats`, { timeout: 20000 }).then((res) => {
        if (cancelled() || !res.data) return;
        setPlatform((prev) => {
          const next = res.data;
          if (!prev) return next;
          const keep = (f, src) => (next.sources?.[src]?.ok === false ? prev[f] : next[f] ?? prev[f]);
          return {
            ...next,
            totalVolume: keep('totalVolume', 'volume'),
            volume1h: keep('volume1h', 'volume'),
            orderBookDepth: keep('orderBookDepth', 'orderBookDepth'),
            hdvl: keep('hdvl', 'hdvl')
          };
        });
      }),
    30000
  );

  // Same rule as the market picker: once /market-tickers has answered, a
  // failed poll keeps the last good numbers instead of switching to
  // last-fill prices from /most-active.
  const tickersOk = React.useRef(false);
  usePoll(
    (cancelled) =>
      axios
        .get(`${url}/hootdex/market-tickers`, { timeout: 15000 })
        .then((res) => {
          if (cancelled() || !Array.isArray(res.data?.tokens) || !res.data.tokens.length) return;
          tickersOk.current = true;
          setTickers(res.data.tokens);
        })
        .catch(() => {
          if (cancelled() || tickersOk.current) return undefined;
          return axios
            .get(`${url}/hootdex/most-active`, { params: { limit: 100 }, timeout: 15000 })
            .then((res) => {
              if (!cancelled() && !tickersOk.current && Array.isArray(res.data?.tokens)) setTickers(res.data.tokens);
            });
        }),
    15000
  );

  // Tradable markets only (wraps don't trade).
  const meta = useMemo(() => {
    const m = new Map();
    ['synthetics', 'crypto_pairs', 'hedge', 'dbt', 'project_tokens', 'holding_tokens', 'fan_tokens', 'xmg_tokens', 'equitrack_tokens'].forEach(
      (k) => (tokenList?.[k] || []).forEach((t) => t?.symbol && m.set(t.symbol, true))
    );
    return m;
  }, [tokenList]);

  const markets = useMemo(() => {
    const listed = meta.size ? tickers.filter((t) => meta.has(t.symbol)) : tickers;
    return listed
      .filter((t) => num(t.price) && (num(t.volume24h) || 0) > 0)
      .sort((a, b) => (num(b.volume24h) || 0) - (num(a.volume24h) || 0));
  }, [tickers, meta]);

  const movers = useMemo(() => {
    const withChg = markets.filter((t) => num(t.changePct) !== null);
    if (!withChg.length) return {};
    const sorted = [...withChg].sort((a, b) => b.changePct - a.changePct);
    return { gainer: sorted[0], loser: sorted[sorted.length - 1] };
  }, [markets]);

  const quoteOf = (s) => tickers.find((t) => t.symbol === s)?.quote || quoteSymbolFor(s);

  const stats = [
    { k: 'v24', label: '24H Volume', value: fmtUsd(platform?.totalVolume?.value, { compact: true }), chg: platform?.totalVolume?.changePct, raw: platform?.totalVolume?.value },
    { k: 'v1', label: '1H Volume', value: fmtUsd(platform?.volume1h?.value, { compact: true }), chg: platform?.volume1h?.changePct, raw: platform?.volume1h?.value },
    { k: 'tr', label: '24H Trades', value: num(platform?.totalVolume?.trades) !== null ? Number(platform.totalVolume.trades).toLocaleString('en-US') : null, raw: platform?.totalVolume?.trades },
    { k: 'obd', label: 'Order Book Depth', value: fmtUsd(platform?.orderBookDepth?.value, { compact: true }), chg: platform?.orderBookDepth?.changePct, raw: platform?.orderBookDepth?.value },
    { k: 'hdvl', label: 'HD Vault Liquidity', value: fmtUsd(platform?.hdvl?.value, { compact: true }), chg: platform?.hdvl?.changePct, raw: platform?.hdvl?.value },
    { k: 'pecu', label: 'PECU', value: num(PecuPrice) ? fmtUsd(PecuPrice) : null, raw: PecuPrice },
    { k: 'mk', label: 'Active Markets', value: markets.length ? String(markets.length) : null, raw: markets.length || null }
  ].filter((s) => num(s.raw) !== null && s.value && s.value !== '—');

  const MarketItem = ({ t, prefix }) => (
    <button type="button" className="x-mq-item x-mq-market" onClick={() => navigate(`/${t.symbol}`)}>
      {prefix && <span className="x-mq-label">{prefix}</span>}
      <b>
        {t.symbol}
        <span className="x-muted">/{quoteOf(t.symbol)}</span>
      </b>
      <span className="x-mq-val">{fmtPrice(t.price)}</span>
      <Change v={t.changePct} />
    </button>
  );

  const row = (copy) => (
    <span className="x-mq-row" aria-hidden={copy ? 'true' : undefined}>
      {stats.map((s) => (
        <span key={s.k} className="x-mq-item">
          <span className="x-mq-label">{s.label}</span>
          <span className="x-mq-val">{s.value}</span>
          <Change v={s.chg} />
        </span>
      ))}
      {movers.gainer && <MarketItem t={movers.gainer} prefix="Top gainer" />}
      {movers.loser && movers.loser !== movers.gainer && <MarketItem t={movers.loser} prefix="Top loser" />}
      {markets.slice(0, MAX_MARKETS).map((t) => (
        <MarketItem key={t.symbol} t={t} />
      ))}
    </span>
  );

  if (!stats.length && !markets.length) return null;

  return (
    <div className="x-mq" role="region" aria-label="Exchange market ticker">
      <span className="x-mq-live">
        <i /> LIVE
      </span>
      <div className="x-mq-viewport">
        <div className="x-mq-track" style={{ animationDuration: `${Math.max(40, (stats.length + markets.length) * 4)}s` }}>
          {row(false)}
          {row(true)}
        </div>
      </div>
    </div>
  );
}
